/**
 * Auth Helper
 * Provides utility functions for retrieving authentication tokens
 */

import { msalInstance } from '../index';
import { apiRequest } from '../config/authConfig';
import { InteractionRequiredAuthError, BrowserAuthError, BrowserUtils } from '@azure/msal-browser';

// Token acquisition lock to prevent multiple concurrent popup attempts
let tokenAcquisitionPromise: Promise<string | null> | null = null;

/**
 * Get access token for API calls
 * Silently acquires token or prompts user if needed
 * Uses a lock to prevent multiple concurrent popup attempts
 * Includes retry logic for initial token acquisition after login
 */
export async function getAccessToken(): Promise<string | null> {
  // If a token acquisition is already in progress, wait for it
  if (tokenAcquisitionPromise) {
    return tokenAcquisitionPromise;
  }

  // Create and store the acquisition promise
  tokenAcquisitionPromise = acquireTokenInternal();

  try {
    const token = await tokenAcquisitionPromise;
    return token;
  } finally {
    // Clear the lock after completion (success or failure)
    tokenAcquisitionPromise = null;
  }
}

/**
 * Internal token acquisition logic with retry for initial login
 */
async function acquireTokenInternal(): Promise<string | null> {
  // Retry logic for initial token acquisition after login
  const maxRetries = 3;
  const retryDelay = 500; // milliseconds

  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      const accounts = msalInstance.getAllAccounts();

      if (accounts.length === 0) {
        // If no accounts yet and this isn't the last attempt, wait and retry
        // This handles the race condition after login
        if (attempt < maxRetries - 1) {
          await new Promise(resolve => setTimeout(resolve, retryDelay));
          continue;
        }
        return null;
      }

      const account = accounts[0];

      try {
        // Try silent token acquisition first
        const response = await msalInstance.acquireTokenSilent({
          ...apiRequest,
          account: account
        });

        return response.accessToken;
      } catch (error) {
        // If silent acquisition fails due to interaction required OR browser/iframe issues,
        // try interactive authentication. BrowserAuthError includes iframe timeouts from cookie blocking.
        if (error instanceof InteractionRequiredAuthError || error instanceof BrowserAuthError) {
          // Check if we're running in a popup - if so, don't try to open another popup
          // This prevents the "block_nested_popups" error
          if (BrowserUtils.isInPopup() || window.opener) {
            console.log('Running in popup context - cannot acquire token interactively');
            return null;
          }

          try {
            console.log('Silent token acquisition failed, falling back to interactive login...');
            const response = await msalInstance.acquireTokenPopup({
              ...apiRequest,
              account: account
            });
            return response.accessToken;
          } catch (popupError: unknown) {
            // If popup fails due to nested popup error, don't retry - just return null
            if (popupError instanceof BrowserAuthError &&
                popupError.errorCode === 'block_nested_popups') {
              console.log('Nested popup detected - returning null');
              return null;
            }
            console.error('Interactive token acquisition also failed:', popupError);
            // For other popup errors, retry if attempts remain
            if (attempt < maxRetries - 1) {
              await new Promise(resolve => setTimeout(resolve, retryDelay));
              continue;
            }
            return null;
          }
        }

        // For other errors, if not the last attempt, wait and retry
        if (attempt < maxRetries - 1) {
          console.log(`Token acquisition attempt ${attempt + 1} failed, retrying...`);
          await new Promise(resolve => setTimeout(resolve, retryDelay));
          continue;
        }

        console.error('Token acquisition failed after retries:', error);
        return null;
      }
    } catch (error) {
      // For unexpected errors, if not the last attempt, wait and retry
      if (attempt < maxRetries - 1) {
        console.log(`Unexpected error in attempt ${attempt + 1}, retrying...`);
        await new Promise(resolve => setTimeout(resolve, retryDelay));
        continue;
      }

      console.error('Failed to get access token after retries:', error);
      return null;
    }
  }

  return null;
}

/**
 * Get authorization header with Bearer token
 */
export async function getAuthHeader(): Promise<Record<string, string>> {
  const token = await getAccessToken();
  
  if (token) {
    return { 'Authorization': `Bearer ${token}` };
  }
  
  return {};
}
